import { z } from 'zod';

import { SessionRatingValueSchema } from '../conversation/session_rating';

/**
 * In-app feedback the analytics-api stores and throttles (issue #7339): the 0–10 experience
 * score after an outcome (`nps`, the legacy rating's name for it), the light trip, wallet and
 * card cards, and the reason for leaving to the classic app. The score asks how the experience
 * was, never whether the traveler would recommend it (a company product): its bands are
 * low/mid/high, not NPS promoters and detractors. Wire between app-integration/agents and
 * analytics-api `/feedback`.
 */

/**
 * Products the score covers. Same vocabulary as the legacy `RatingLocation` (voll-apollo
 * rating subgraph): every answer is mirrored there, and the classic app's ratings use it.
 */
export const NpsProductSchema = z.enum([
  'FLIGHT',
  'HOTEL',
  'RENTAL',
  'BUS',
  'MOBILITY',
  'SCHEDULER_RIDE',
  'PAYMENT',
  'EXPENSE',
  'EXPENSE_KM',
]);
export type NpsProduct = z.infer<typeof NpsProductSchema>;

export const FeedbackSurfaceSchema = z.enum(['nps', 'trip', 'wallet', 'card', 'classic_exit']);
export type FeedbackSurface = z.infer<typeof FeedbackSurfaceSchema>;

/** Why the traveler goes back to the classic app. The first two are the negative ones. */
export const ClassicExitReasonSchema = z.enum([
  'missing_feature',
  'error',
  'prefer_classic',
  'just_checking',
]);
export type ClassicExitReason = z.infer<typeof ClassicExitReasonSchema>;

/** A score of 0–6 is low: the only scores that open the comment field. */
export const NPS_LOW_MAX = 6;
export const FEEDBACK_COMMENT_MAX = 1000;

/** What is being rated. The trip card is asked once per trip, so it carries the trip. */
export const FeedbackSubjectSchema = z.discriminatedUnion('surface', [
  z.object({ surface: z.literal('nps'), product: NpsProductSchema }).strict(),
  z.object({ surface: z.literal('trip'), tripId: z.string().min(1).max(128) }).strict(),
  z.object({ surface: z.literal('wallet') }).strict(),
  z.object({ surface: z.literal('card') }).strict(),
  z.object({ surface: z.literal('classic_exit') }).strict(),
]);
export type FeedbackSubject = z.infer<typeof FeedbackSubjectSchema>;

/** The stored key of a subject: the product, the trip id, or the surface itself. */
export function feedbackSubjectKey(subject: FeedbackSubject): string {
  if (subject.surface === 'nps') return subject.product;
  if (subject.surface === 'trip') return subject.tripId;
  return subject.surface;
}

/** The traveler, by the public id that leaves the BFF (never the legacy user id). */
export const FeedbackTravelerSchema = z
  .object({
    ref: z.string().min(1).max(128),
    companyId: z.string().min(1).max(64).nullable(),
  })
  .strict();
export type FeedbackTraveler = z.infer<typeof FeedbackTravelerSchema>;

const CommentSchema = z.string().trim().min(1).max(FEEDBACK_COMMENT_MAX).optional();

export const FeedbackAnswerSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('nps'),
      rate: z.number().int().min(0).max(10),
      comment: CommentSchema,
    })
    .strict(),
  z
    .object({ kind: z.literal('quick'), value: SessionRatingValueSchema, comment: CommentSchema })
    .strict(),
  z
    .object({
      kind: z.literal('classic_exit'),
      reason: ClassicExitReasonSchema,
      comment: CommentSchema,
    })
    .strict(),
  z.object({ kind: z.literal('dismissed') }).strict(),
]);
export type FeedbackAnswer = z.infer<typeof FeedbackAnswerSchema>;

/** A negative answer: the only kind that may carry a comment. */
export function isNegativeFeedback(answer: FeedbackAnswer): boolean {
  if (answer.kind === 'nps') return answer.rate <= NPS_LOW_MAX;
  if (answer.kind === 'quick') return answer.value === 'bad';
  if (answer.kind === 'classic_exit') {
    return answer.reason === 'missing_feature' || answer.reason === 'error';
  }
  return false;
}

const ANSWER_KIND_OF: Record<FeedbackSurface, FeedbackAnswer['kind']> = {
  nps: 'nps',
  trip: 'quick',
  wallet: 'quick',
  card: 'quick',
  classic_exit: 'classic_exit',
};

export const FeedbackInviteRequestSchema = z
  .object({ traveler: FeedbackTravelerSchema, subject: FeedbackSubjectSchema })
  .strict();
export type FeedbackInviteRequest = z.infer<typeof FeedbackInviteRequestSchema>;

export const FeedbackInviteResponseSchema = z.object({ invite: z.boolean() }).strict();
export type FeedbackInviteResponse = z.infer<typeof FeedbackInviteResponseSchema>;

/**
 * An answer to a subject: the answer kind matches the surface (or is `dismissed`), and a
 * comment only comes with a negative answer. Never compose with `.and(...)`: the zod
 * intersection drops the refinement.
 */
export const FeedbackResponseRequestSchema = z
  .object({
    traveler: FeedbackTravelerSchema,
    subject: FeedbackSubjectSchema,
    answer: FeedbackAnswerSchema,
  })
  .strict()
  .superRefine((req, ctx) => {
    const { answer, subject } = req;
    if (answer.kind !== 'dismissed' && answer.kind !== ANSWER_KIND_OF[subject.surface]) {
      ctx.addIssue({
        code: 'custom',
        path: ['answer', 'kind'],
        message: 'answer_surface_mismatch',
      });
    }
    if ('comment' in answer && answer.comment !== undefined && !isNegativeFeedback(answer)) {
      ctx.addIssue({ code: 'custom', path: ['answer', 'comment'], message: 'comment_not_allowed' });
    }
  });
export type FeedbackResponseRequest = z.infer<typeof FeedbackResponseRequestSchema>;

/** Indicator dim `feedback_subject`: the scored product, or the surface (a trip id never becomes a dim). */
export const FeedbackSubjectDimSchema = z.enum([
  ...NpsProductSchema.options,
  'trip',
  'wallet',
  'card',
  'classic_exit',
]);
export type FeedbackSubjectDim = z.infer<typeof FeedbackSubjectDimSchema>;

/** Indicator dim `feedback_answer`: an invite (`offered`), its dismissal, the score band, the
 *  light card's value or the classic-exit reason. */
export const FeedbackAnswerDimSchema = z.enum([
  'offered',
  'dismissed',
  'low',
  'mid',
  'high',
  ...SessionRatingValueSchema.options,
  ...ClassicExitReasonSchema.options,
]);
export type FeedbackAnswerDim = z.infer<typeof FeedbackAnswerDimSchema>;

/** Band of a 0–10 score: 9–10 high, 7–8 mid, 0–6 low (the NPS cuts, so the legacy numbers
 *  still compare). */
export function npsBand(rate: number): 'low' | 'mid' | 'high' {
  if (rate >= 9) return 'high';
  if (rate > NPS_LOW_MAX) return 'mid';
  return 'low';
}
